import { useContext } from 'react'
import PropTypes from 'prop-types'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, Line, Link, ModalContext, Search, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { confirmDelete } from '@functions/entity'
import searchFn from '@functions/search'

import EntitySelector from '@components/EntitySelector'

import useCreateOrEditAgencyProviderModal from './hooks/useCreateOrEditAgencyProviderModal'

const CreateOrEditAgencyProviderModal = (props) => {
  const {
    callbacks, hideAgencyAndBrand = false, modalKey = 'CreateOrEditAgencyProviderModal', showModal,
  } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData, topModalKey } = modalContext

  const isTopModal = topModalKey === modalKey

  const modalPayload = digObject(modalData, modalKey, {})
  const { agencyProvider } = modalPayload

  const {
    agency,
    agencyProviderForm,
    assignedProviderIds,
    callbacks: {
      addBrandProvider,
      createAgencyProvider: createFn,
      createOrUpdateAgencyProvider,
      deleteAgencyProvider: deleteFn,
      updateAgencyProvider: updateFn,
      handleChangeAgencyId,
      handleChangeBrandId,
      handleChangePriceListId,
      handleChangeProviderId,
      handleChangeServiceId,
      loadMorePriceLists,
      mapResults,
    },
    canLoadMore,
    creating,
    currentUserPayload,
    deleting,
    filteredProviderPriceLists,
    filteredServices,
    selectedPriceListId,
    updating,
  } = useCreateOrEditAgencyProviderModal(agencyProvider)

  const { isAdmin, isProviderDirector } = currentUserPayload || {}

  const {
    entityState,
    entityState: {
      agency_id, brandId, id, identity_code, provider_id, provider_price_list_id, service_id,
    },
    handlers,
    saveEnabled,
  } = agencyProviderForm

  const saveOrCreateButtonText = id ? 'Save Changes' : `Add ${isProviderDirector ? 'Agency' : 'Provider'}`

  const confirmDeleteParams = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      deleteString: 'remove',
      entityName: 'Provider',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title={id ? 'Edit' : 'Add'}
        titleSecondLine={isProviderDirector ? 'Agency' : 'Provider'}
      />

      <SidebarModal.Content>
        <Form>
          {isProviderDirector && !hideAgencyAndBrand && (
            <>
              <EntitySelector
                callbacks={{
                  selectItem: brand => handleChangeBrandId(brand?.id),
                }}
                entityKey="brands"
                label="* Brand"
                selectedItemId={brandId}
              />

              {!!brandId && (
                <Form.Field label="* Agency" marginTop="large">
                  <Search
                    callbacks={{
                      mapResults: agencies => mapResults({ agencies }),
                      search: searchFn,
                      selectItem: (targetAgency) => { handleChangeAgencyId(targetAgency) },
                    }}
                    debounceInterval={300}
                    disabled={!brandId}
                    endpoint="agencies.json"
                    forwardProps={{
                      closeOnSelect: true,
                      setStringOnSelect: true,
                      showOnFocus: true,
                    }}
                    requestOptions={{ brand_id: brandId, brand_provider_agencies: true }}
                  />
                </Form.Field>
              )}
            </>
          )}

          {agency_id && isProviderDirector && !hideAgencyAndBrand && (
            <Line />
          )}

          {((isProviderDirector && agency_id) || isAdmin) && (
            <>
              <EntitySelector
                callbacks={{ selectItem: priceList => handleChangePriceListId(priceList) }}
                endpoint="provider_price_lists"
                entityKey="providerPriceLists"
                label="* Find by Price List"
                requestOptions={{ include_provider_price_list_provider: true }}
                searchForwardProps={{
                  listAction: isAdmin && (
                    <EntitySelector.CreateEntity
                      onClick={() => addBrandProvider()}
                      strings={{ create: 'Assign to Brand to enable' }}
                    />
                  ),
                }}
                searchRequestOptions={{ assigned_brand_id: agency.brand_id }}
                selectedItemId={selectedPriceListId}
                shouldRefetch={isTopModal}
                showEntityId
              />

              <SectionDivider boxProps={{ marginY: 'medium' }}>OR</SectionDivider>
            </>
          )}

          {(!isProviderDirector
           || (isProviderDirector && agency_id)
            || (isProviderDirector && selectedPriceListId)) && (
            <Form.Field label="* Service" marginBottom="large">
              <select
                disabled={selectedPriceListId}
                onChange={e => handleChangeServiceId(e.target.value, isProviderDirector)}
                value={service_id}
              >
                <option value="">Please Select ...</option>
                {Object.values(filteredServices).map(service => (
                  <option key={service.id} value={service.id}>
                    {service.title}
                  </option>
                ))}
              </select>
            </Form.Field>
          )}

          {service_id && !isProviderDirector && (
            <EntitySelector
              boxProps={{ marginTop: 'large' }}
              callbacks={{ selectItem: provider => handleChangeProviderId(provider?.id) }}
              disabled={!!selectedPriceListId}
              entityKey="providers"
              label="* Provider"
              requestOptions={{ include_provider_price_lists: true }}
              searchForwardProps={{
                listAction: (
                  <EntitySelector.CreateEntity
                    onClick={() => addBrandProvider()}
                    strings={{ create: 'Assign to Brand to enable' }}
                  />
                ),
              }}
              searchRequestOptions={{ assigned_brand_id: agency.brand_id, service_id }}
              selectedIds={assignedProviderIds}
              selectedItemId={provider_id}
              shouldRefetch={isTopModal}
            />
          )}

          {((!isProviderDirector && provider_id && service_id) || (isProviderDirector && service_id)) && (
            <>
              <Form.Field
                labelRight={canLoadMore
                  ? <Link onClick={() => loadMorePriceLists()} textProps={{ fontSize: 'small' }}>Load More</Link>
                  : null}
                label="* Price List"
                marginTop="large"
              >
                <select
                  disabled={!!selectedPriceListId}
                  name="provider_price_list_id"
                  value={provider_price_list_id}
                  {...handlers}
                >
                  <option value="">Default</option>
                  {filteredProviderPriceLists.map(providerPriceList => (
                    <option key={providerPriceList.id} value={providerPriceList.id}>
                      {providerPriceList.default
                        ? `${providerPriceList.title} (Default)`
                        : `${providerPriceList.title}`}
                    </option>
                  ))}
                </select>
              </Form.Field>

              <Form.Field label="Identity Code" marginTop="large">
                <input name="identity_code" type="text" value={identity_code} {...handlers} />
              </Form.Field>
            </>
          )}

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(confirmDeleteParams)}
                size="medium"
              >
                Remove Provider
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateAgencyProvider({
            isProviderDirector, entityState, hideAgencyAndBrand, props, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditAgencyProviderModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  hideAgencyAndBrand: PropTypes.bool,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditAgencyProviderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
